import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import clsx from 'clsx';

export default function CICDStep() {
  const { cicd, updateCICD } = useWizardStore();
  const { options, loading } = useWizardOptions();

  const ciPlatforms = options?.cicd.ciPlatforms ?? [];
  const pipelineStages = options?.cicd.pipelineStages ?? [];
  const autoDeployOptions = options?.cicd.autoDeployOptions ?? [];

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const toggleStage = (stage: string) => {
    const current = cicd.stages;
    if (current.includes(stage)) {
      updateCICD({ stages: current.filter((s) => s !== stage) });
    } else {
      updateCICD({ stages: [...current, stage] });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          CI/CD Configuration
        </h2>
        <p className="text-sm text-gray-500">
          Configure your continuous integration and deployment pipeline.
        </p>
      </div>

      {/* CI Platform */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          CI Platform
        </label>
        <select
          value={cicd.platform}
          onChange={(e) => updateCICD({ platform: e.target.value })}
          className="select-field"
        >
          <option value="">Select CI platform...</option>
          {ciPlatforms.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Pipeline Stages */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Pipeline Stages
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Select the stages in your pipeline. They will be configured in the
          selected order.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {pipelineStages.map((stage) => {
            const isSelected = cicd.stages.includes(stage);
            const orderNum = isSelected
              ? cicd.stages.indexOf(stage) + 1
              : null;

            return (
              <button
                key={stage}
                onClick={() => toggleStage(stage)}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 text-sm text-left',
                  isSelected
                    ? 'bg-accent/10 border-accent/30 text-accent-300'
                    : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
                )}
              >
                <div
                  className={clsx(
                    'w-6 h-6 rounded flex items-center justify-center shrink-0 text-xs font-bold',
                    isSelected
                      ? 'bg-accent text-white'
                      : 'bg-surface-200 text-gray-500'
                  )}
                >
                  {orderNum ?? '-'}
                </div>
                {stage}
              </button>
            );
          })}
        </div>
        {cicd.stages.length > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-surface-DEFAULT border border-dark-border">
            <span className="text-xs text-gray-500">Pipeline order: </span>
            <span className="text-xs text-gray-300 font-mono">
              {cicd.stages.join(' → ')}
            </span>
          </div>
        )}
      </div>

      {/* Auto Deploy */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Auto-Deploy Strategy
        </label>
        <div className="grid gap-2">
          {autoDeployOptions.map((option) => (
            <label
              key={option.value}
              className={clsx(
                'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200',
                cicd.autoDeploy === option.value
                  ? 'bg-accent/10 border-accent/30'
                  : 'bg-surface-DEFAULT border-dark-border hover:border-surface-300'
              )}
            >
              <input
                type="radio"
                name="autoDeploy"
                value={option.value}
                checked={cicd.autoDeploy === option.value}
                onChange={(e) => updateCICD({ autoDeploy: e.target.value })}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 transition-colors',
                  cicd.autoDeploy === option.value
                    ? 'border-accent bg-accent'
                    : 'border-surface-300'
                )}
              >
                {cicd.autoDeploy === option.value && (
                  <div className="w-full h-full rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm font-medium text-white">
                  {option.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {option.desc}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* AI Code Flag */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-surface-DEFAULT border border-dark-border">
        <div>
          <span className="text-sm font-medium text-gray-300">
            AI Code Flag
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Automatically flag AI-generated code in commits and PRs
          </p>
        </div>
        <button
          onClick={() => updateCICD({ aiCodeFlag: !cicd.aiCodeFlag })}
          className={clsx(
            'relative w-11 h-6 rounded-full transition-colors duration-200',
            cicd.aiCodeFlag ? 'bg-accent' : 'bg-surface-300'
          )}
        >
          <div
            className={clsx(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
              cicd.aiCodeFlag && 'translate-x-5'
            )}
          />
        </button>
      </div>

      {/* Code Ownership */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-surface-DEFAULT border border-dark-border">
        <div>
          <span className="text-sm font-medium text-gray-300">
            Code Ownership
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Generate CODEOWNERS file based on team structure
          </p>
        </div>
        <button
          onClick={() =>
            updateCICD({ codeOwnership: !cicd.codeOwnership })
          }
          className={clsx(
            'relative w-11 h-6 rounded-full transition-colors duration-200',
            cicd.codeOwnership ? 'bg-accent' : 'bg-surface-300'
          )}
        >
          <div
            className={clsx(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
              cicd.codeOwnership && 'translate-x-5'
            )}
          />
        </button>
      </div>
    </div>
  );
}
